import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function HubServices() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/seo/services");
        setServices(res.data?.services || []);
      } catch (_e) {
        /* graceful */
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#FBFAF6]">
      <Helmet>
        <title>Window Screen Services — WindowScreenQuote</title>
        <meta name="description" content="Get instant pricing for window screen repair, new screens, rescreens, sliding door screens, retractable screens, and security screen doors." />
        <link rel="canonical" href="https://windowscreenquote.com/services" />
      </Helmet>

      <nav className="px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between border-b border-slate-200/60 bg-white/80 backdrop-blur sticky top-0 z-40">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo size={36} withWordmark />
        </Link>
        <Link to="/" className="text-sm font-semibold text-slate-600 hover:text-slate-900">Home</Link>
      </nav>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-14 lg:py-20" data-testid="services-hub">
        <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-3">Our Services</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">Window screen services</h1>
        <p className="text-slate-600 mt-4 text-lg max-w-2xl leading-relaxed">
          Honest, upfront pricing on every common screen job — from a simple rescreen to a brand-new security door.
        </p>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/quote"><Button size="lg" className="bg-[#0F172A] hover:bg-black rounded-2xl h-12 px-6 text-sm font-bold" data-testid="services-quote-cta">Get an instant quote <ArrowRight className="w-4 h-4 ml-1.5" /></Button></Link>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 gap-4">
          {services.map((s) => (
            <Link
              key={s.slug}
              to={`/${s.slug}`}
              className="block bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md hover:-translate-y-0.5 transition-all"
              data-testid={`hub-service-${s.slug}`}
            >
              <div className="font-display font-bold text-slate-900 text-lg">{s.name}</div>
              <div className="text-sm text-slate-600 mt-1.5 leading-relaxed">{s.summary}</div>
              <div className="text-xs text-[#C75D33] font-semibold mt-4 inline-flex items-center gap-1">
                See pricing <ArrowRight className="w-3 h-3" />
              </div>
            </Link>
          ))}
        </div>
      </main>

      <footer className="border-t border-slate-200 py-8 px-5 sm:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between gap-3 text-sm text-slate-500">
          <span>© WindowScreenQuote.com {new Date().getFullYear()}</span>
          <div className="flex flex-wrap gap-5">
            <Link to="/" className="hover:text-slate-900">Home</Link>
            <Link to="/locations" className="hover:text-slate-900">Locations</Link>
            <Link to="/terms" className="hover:text-slate-900">Terms</Link>
            <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
